'use client';

import { useKindeBrowserClient } from '@kinde-oss/kinde-auth-nextjs';
import { LinkTitle, RowItem } from './components';
import useSWR from 'swr';
import { fetcher } from '@/lib/fetcher';
import { useEffect, useState } from 'react';
import { Product } from '@/services/products/products';
import {
  Carousel,
  CarouselApi,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from '@/components/ui/carousel';
import { DefaultBikeIcon } from '@/components/icons';
import { displayValue } from '@/lib/utils';

export const MyBicyclesWidget = () => {
  const { accessTokenRaw, isLoading: isKindeLoading } = useKindeBrowserClient();

  const [api, setApi] = useState<CarouselApi>();
  const [currentIndex, setCurrentIndex] = useState(0);

  // Defer fetching until accessTokenRaw is available
  const {
    data: products,
    error: error,
    isLoading: isLoading,
  } = useSWR(
    accessTokenRaw ? ['/v1/products', accessTokenRaw] : null,
    ([url, token]) => fetcher(url, token),
  );

  useEffect(() => {
    if (!api) return;

    const handleSelect = () => {
      setCurrentIndex(api.selectedScrollSnap());
    };

    api.on('select', handleSelect);
    return () => {
      api.off('select', handleSelect);
    };
  }, [api]);

  // Handle loading and error states
  if (isKindeLoading || isLoading) return <LoadingState />;
  if (error || !accessTokenRaw) return <ErrorState />;

  const currentBike: Product | undefined = products[currentIndex];

  return (
    <div className="col-span-1 row-span-2 bg-white rounded-3xl p-4 lg:p-6 select-none">
      <LinkTitle title="Bekijk alle fietsen" href="/mijn-fietsen" />
      <div className="relative w-full">
        <div className="flex justify-center">
          <Carousel className="w-full max-w-md lg:my-10" setApi={setApi}>
            <div className="absolute left-0 top-0 w-12 h-full bg-[linear-gradient(to_right,white_0%,white_75%,transparent_100%)] z-10" />
            <CarouselContent className="snap-mandatory snap-x">
              {products?.map((product: Product) => (
                <CarouselItem
                  key={product.id}
                  className="snap-start md:basis-full lg:basis-full"
                >
                  <div className="p-1">
                    {product.image ? (
                      <img
                        src={product.image}
                        alt={product.brand + ' ' + product.type}
                        className="aspect-square object-contain rounded-md w-full h-32 lg:h-48 pointer-events-none"
                        draggable="false"
                      />
                    ) : (
                      <DefaultBikeIcon className="w-full h-32 lg:h-48 pointer-events-none" />
                    )}
                    <div className="font-bold text-lg text-center mt-2">
                      {product.brand} <br /> {product.type}
                    </div>
                  </div>
                </CarouselItem>
              ))}
            </CarouselContent>
            <CarouselPrevious className="z-20 absolute left-0 lg:left-4 bg-white" />
            <CarouselNext className="z-20 absolute right-0 lg:right-4 bg-white" />
            <div className="absolute right-0 top-0 w-12 h-full bg-[linear-gradient(to_left,white_0%,white_75%,transparent_100%)] z-10" />
          </Carousel>
        </div>
      </div>
      <div className="hidden lg:block space-y-3">
        <RowItem
          title="Merk"
          value={<span>{displayValue(currentBike?.brand)}</span>}
        />
        <RowItem
          title="Type"
          value={<div>{displayValue(currentBike?.type)}</div>}
        />
        <RowItem
          title="Framenummer"
          value={<div>{displayValue(currentBike?.frameNumber)}</div>}
        />
        <RowItem
          title="Maat"
          value={<div>{displayValue(currentBike?.size)}</div>}
        />
        <RowItem
          title="Kleur"
          value={<div>{displayValue(currentBike?.color)}</div>}
        />
        <RowItem
          title="Sleutelnummer 1"
          value={<div>{displayValue(currentBike?.keyNumber1)}</div>}
        />
        <RowItem
          title="Sleutelnummer 2"
          value={<div>{displayValue(currentBike?.keyNumber2)}</div>}
        />
        <RowItem
          title="Batterijnummer"
          value={<div>{displayValue(currentBike?.batteryNumber)}</div>}
        />
        <RowItem
          title="Towo Code"
          value={<div>{displayValue(currentBike?.towoCode)}</div>}
        />
      </div>
    </div>
  );
};

const LoadingState = () => {
  return (
    <div className="col-span-1 row-span-2 bg-white rounded-3xl p-4 lg:p-6 flex flex-col">
      <LinkTitle title="Bekijk alle fietsen" href="/mijn-fietsen" />
      <div className="relative w-full flex justify-center">
        <div className="absolute left-0 top-0 w-12 h-full bg-[linear-gradient(to_right,white_0%,white_75%,transparent_100%)] z-10" />
        <div className="w-full max-w-md lg:my-10">
          <div className="animate-pulse bg-gray-100 rounded-md w-full h-32 lg:h-48">
            &nbsp;
          </div>
          <div className="animate-pulse font-bold text-lg text-center mt-2">
            <div className="bg-gray-100 h-6 w-48 mx-auto rounded-md">
              &nbsp;
            </div>
            <div className="bg-gray-100 h-6 w-32 mx-auto rounded-md mt-2">
              &nbsp;
            </div>
          </div>
        </div>
        <div className="absolute right-0 top-0 w-12 h-full bg-[linear-gradient(to_left,white_0%,white_75%,transparent_100%)] z-10" />
      </div>
      <div className="hidden lg:block space-y-3">
        <div className="animate-pulse">
          <RowItem
            title="Merk"
            value={
              <div className="bg-gray-100 h-4 w-32 rounded-md">&nbsp;</div>
            }
          />
        </div>
        <div className="animate-pulse">
          <RowItem
            title="Type"
            value={
              <div className="bg-gray-100 h-4 w-32 rounded-md">&nbsp;</div>
            }
          />
        </div>
        <div className="animate-pulse">
          <RowItem
            title="Framenummer"
            value={
              <div className="bg-gray-100 h-4 w-32 rounded-md">&nbsp;</div>
            }
          />
        </div>
        <div className="animate-pulse">
          <RowItem
            title="Maat"
            value={
              <div className="bg-gray-100 h-4 w-32 rounded-md">&nbsp;</div>
            }
          />
        </div>
        <div className="animate-pulse">
          <RowItem
            title="Kleur"
            value={
              <div className="bg-gray-100 h-4 w-32 rounded-md">&nbsp;</div>
            }
          />
        </div>
        <div className="animate-pulse">
          <RowItem
            title="Sleutelnummer 1"
            value={
              <div className="bg-gray-100 h-4 w-32 rounded-md">&nbsp;</div>
            }
          />
        </div>
        <div className="animate-pulse">
          <RowItem
            title="Sleutelnummer 2"
            value={
              <div className="bg-gray-100 h-4 w-32 rounded-md">&nbsp;</div>
            }
          />
        </div>
        <div className="animate-pulse">
          <RowItem
            title="Batterijnummer"
            value={
              <div className="bg-gray-100 h-4 w-32 rounded-md">&nbsp;</div>
            }
          />
        </div>
        <div className="animate-pulse">
          <RowItem
            title="Towo Code"
            value={
              <div className="bg-gray-100 h-4 w-32 rounded-md">&nbsp;</div>
            }
          />
        </div>
      </div>
    </div>
  );
};

const ErrorState = () => {
  return (
    <div className="col-span-1 row-span-2 bg-white rounded-3xl p-6">
      Er is een fout opgetreden
    </div>
  );
};
